import React, { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { max } from 'lib/array/arrayUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { generateOfferPageRoom, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import ModalContext from 'contexts/ModalContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import Pill from 'LuxKit/Pill'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { useIntl } from 'react-intl'

interface Props {
  offer: App.Offer
  packageView: App.OfferPackageOptionView
  rooms?: Array<OfferPageRoom>
  checkIn?: string
  fetching?: boolean
  currency: string
  isSpoofed: boolean
  defaultDepositAmountPercentage: number
  priceMode: OfferPagePriceMode
}

const defaultRooms = [generateOfferPageRoom()]

function FlashHotelOptionPriceDetailsBNBLVariant(props: Props) {
  const {
    offer,
    rooms = defaultRooms,
    checkIn,
    fetching,
    currency,
    isSpoofed,
    defaultDepositAmountPercentage,
    packageView,
    priceMode,
  } = props

  const {
    package: pkg,
    flightPrice,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelTotal,
    hotelMemberTotal,
  } = packageView

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const discountPercent = offer.bundledWithFlightsOnly ? bundledDiscountPercent : max([hotelDiscountPercent, bundledDiscountPercent])

  const hasFlightWithCachePrice = !!flightPrice && !offer.flightCacheDisabled
  const shouldShowDiscount = !offer.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && (offer.bundledWithFlightsOnly ? hasFlightWithCachePrice : true)
  const shouldShowValue = hotelValue > hotelPrice && pkg.shouldDisplayValue
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, hotelPrice)
  const isFlexibleCancellation = isFlexibleCancellationPolicy(packageView.package?.roomRate?.cancellationPolicy)

  const roomRateIsReservableForZero = isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const showReserveForZero = isSpoofed && roomRateIsReservableForZero
  const showDepositTag = isDepositEnabled && !isSpoofed
  const showFlexiblePaymentTag = isDepositEnabled && isSpoofed

  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const convertToDisplay = useMemo(() => getPriceDisplayFunction(priceMode, rooms), [priceMode, rooms])
  const baseSaleUnit = priceMode === 'perPerson' ? 'person' : 'total'

  const onFlexPaymentPolicyOpen = useCallback(async() => {
    showModal(
      <ReserveForZeroAndDepositPolicyModal
        depositAmountPercentage={depositAmountPercentage}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />,
    )
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])

  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])

  return <div>
    <Group direction="horizontal" gap={4} wrap="wrap" noDisplayWhenEmpty>
      {showDepositTag && <SecureWithDepositTagAndModal
        isFlexibleCancellation={isFlexibleCancellation}
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
      />}
      {showFlexiblePaymentTag && <Clickable onClick={onFlexPaymentPolicyOpen}>
        <Pill startIcon={<LineClockDollarIcon />} kind="tertiary" variant="primary">
          {intl.formatMessage({
            id: 'BGTFLVcrmg',
            defaultMessage: 'Flexible payment options',
            description: 'Label for flexible payment options',
          })}
        </Pill>
      </Clickable>}
    </Group>
    {!offer.bundledWithFlightsOnly && <Group direction="vertical" verticalAlign="space-between" data-testid="pricing" gap={16}>
      <VerticalSpacer gap={8}>
        <VerticalSpacer gap={4}>
          <div>
            {/* TODO: i18n – 💲 Pricing format: '{n} night(s) from' uses conditional 'from'
                as part of pricing display. Needs coordinated pricing i18n approach. */}
            <PriceRowPriceCaption>
              {pluralizeToString('night', pkg.duration)} {!checkIn && 'from'}
            </PriceRowPriceCaption>
            <LuxPlusPriceStack
              size="L"
              price={convertToDisplay(hotelTotal)}
              memberPrice={convertToDisplay(hotelMemberTotal)}
              saleUnit={baseSaleUnit}
              testid="offer-package-price"
              offerConfig={offer.luxPlus}
            />
            {showIncludedGuestsExceeded && <AdditionalGuestsPopup
              complex={false}
              description={intl.formatMessage({
                id: 'UekksioAOK',
                defaultMessage: '+ Extra guest surcharge',
                description: 'Description for extra guest surcharge',
              })}
              modalContent={pkg.roomPolicyDescription}
            />}
            {shouldShowValue && <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              size="M"
              originalValue={convertToDisplay(hotelValue)}
              discountPercentage={shouldShowDiscount ? discountPercent : undefined}
            />}
            <PriceRowTaxesAndFees />
          </div>
          <PriceRowAgentHubCommission
            offerId={offer.id}
            productType={offer.productType}
            kind="plain"
            packageId={pkg.id}
          />
          {showReserveForZero && <TextLink
            colour="highlight-secondary"
            size="small"
            weight="bold"
            endIcon={<LineInfoCircleIcon colour="primary" />}
            onClick={openReserveForZeroPolicyModal}
          >
            {intl.formatMessage({
              id: 'Qtjnxg++0D',
              defaultMessage: 'Reserve now for free',
              description: 'Link text to reserve now for free',
            })}
          </TextLink>}
        </VerticalSpacer>
      </VerticalSpacer>
    </Group>}

    <LoadingIndicator visible={fetching} floating opaque />
  </div>
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  rooms: state.rooms,
  offer: state.offer,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  isSpoofed: appState.auth.account.isSpoofed,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  priceMode: state.priceMode,
}), FlashHotelOptionPriceDetailsBNBLVariant)
